import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { del, get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

export interface AccountDetails {
  account_id: string;
  account_name: string;
  city: string | null;
  country: string | null;
  region: string | null;
  industry: string | null;
  assessment_level: string | null;
  renewal_date: string | null;
  revenue: number | null;
  tier: string | null;
}

export interface AccountTeamMember {
  id: string;
  customer_id: string;
  user_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  email: string;
  full_name: string;
  phone: string;
  position: string;
  role_name: string;
  grade_id: string;
}

export interface CustomerTeamMember {
  name: string;
  email: string;
  phone: string | null;
  image_url: string | null;
  role: string | null;
  position: string | null;
  customer_type: string | null;
  influence_level: string | null;
  reports_to: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  timezone: string | null;
  language: string | null;
  is_active: boolean;
  user_id: string;
  customer_id: string;
  created_at: string;
  updated_at: string;
}

export interface ExecutiveTeamMember {
  id?: string;
  user_id: string;
  executive_type?: string;
  full_name: string;
  email: string;
  phone: string;
  position: string;
  role_name: string;
  reason?: string | null;
  grade?: string | null;
}

export interface OrganizationUser {
  user_id: string;
  role: string;
  email: string;
  name: string;
  /** Only returned when the endpoint is called with a role_type filter. */
  phone?: string | null;
  position?: string | null;
}

export interface StakeholderRelationshipAssessment {
  id: string;
  account_id?: string;
  user_id?: string;
  contact_id?: string;
  rl: number;
  posture?: string | null;
  rationale?: string | null;
  assessed_at?: string | null;
  target_rl?: number | null;
  target_date?: number | null;
  status?: string | null;
}

export interface StakeholderRelationship {
  relationship_id: string;
  team_type: string;
  account_team_member?: string | null;
  executive_team_member?: string | null;
  customer_team_member: string;
  relationship_percentage?: number | null;
  is_priority?: boolean;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
  latest_assessment?: StakeholderRelationshipAssessment | null;
}

interface AccountTeamState {
  members: AccountTeamMember[];
  loading: boolean;
  error: string | null;
}

interface CustomerTeamState {
  members: CustomerTeamMember[];
  loading: boolean;
  error: string | null;
}

interface AccountDetailsState {
  data: AccountDetails | null;
  loading: boolean;
  error: string | null;
  accountTeam: AccountTeamState;
  customerTeam: CustomerTeamState;
  accountTeamCreate: {
    loading: boolean;
    error: string | null;
    success: boolean;
    message: string | null;
  };
  customerTeamCreate: {
    loading: boolean;
    error: string | null;
    success: boolean;
    message: string | null;
  };
  executiveTeam: {
    sponsors: ExecutiveTeamMember[];
    extraSponsors: ExecutiveTeamMember[];
    organizationUsers: OrganizationUser[];
    loading: boolean;
    organizationUsersLoading: boolean;
    error: string | null;
    organizationUsersError: string | null;
  };
  executiveTeamCreate: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  /** Directory of org users eligible for the account team (role_type=account_team). */
  accountTeamCandidates: {
    items: OrganizationUser[];
    loading: boolean;
    error: string | null;
  };
  stakeholderRelationships: {
    items: StakeholderRelationship[];
    loading: boolean;
    error: string | null;
    saving: boolean;
    saveError: string | null;
  };
  /** Shared loading/error for team member mutations (update/deactivate/delete). */
  memberAction: {
    loading: boolean;
    error: string | null;
  };
}

const initialAccountTeamState: AccountTeamState = {
  members: [],
  loading: false,
  error: null,
};

const initialCustomerTeamState: CustomerTeamState = {
  members: [],
  loading: false,
  error: null,
};

const initialState: AccountDetailsState = {
  data: null,
  loading: false,
  error: null,
  accountTeam: initialAccountTeamState,
  customerTeam: initialCustomerTeamState,
  accountTeamCreate: {
    loading: false,
    error: null,
    success: false,
    message: null,
  },
  customerTeamCreate: {
    loading: false,
    error: null,
    success: false,
    message: null,
  },
  executiveTeam: {
    sponsors: [],
    extraSponsors: [],
    organizationUsers: [],
    loading: false,
    organizationUsersLoading: false,
    error: null,
    organizationUsersError: null,
  },
  executiveTeamCreate: {
    loading: false,
    error: null,
    success: false,
  },
  accountTeamCandidates: {
    items: [],
    loading: false,
    error: null,
  },
  stakeholderRelationships: {
    items: [],
    loading: false,
    error: null,
    saving: false,
    saveError: null,
  },
  memberAction: {
    loading: false,
    error: null,
  },
};

interface CreateAccountTeamMemberPayload {
  email: string;
  full_name: string;
  phone: string;
  position: string;
  role: string;
  grade: number;
  languages: string;
}

interface CreateAccountTeamBulkPayload {
  customerId: string;
  batch_name: string | null;
  approver_id: string;
  members: CreateAccountTeamMemberPayload[];
}

export interface TeamBulkUploadContent {
  batch_upload_id?: string;
  batch_name?: string;
  upload_type?: string;
  record_count?: number;
  status?: string;
  valid?: boolean;
  approver_id?: string;
}

export interface TeamBulkUploadApiResponse {
  content?: TeamBulkUploadContent;
  response?: {
    status?: number;
    message?: string;
  };
  status?: string;
  message?: string;
}

export interface TeamBulkUploadResult {
  content: TeamBulkUploadContent;
  message: string;
}

export interface CreateCustomerTeamMemberPayload {
  name: string;
  email: string;
  phone?: string;
  role?: string;
  position?: string;
  language?: string;
}

interface CreateCustomerTeamPayload extends CreateCustomerTeamMemberPayload {
  customerId: string;
}

interface CreateExtraSponsorsBulkPayload {
  customerId: string;
  members: { user_id: string; reason: string }[];
}

interface SaveStakeholderLinksPayload {
  customerId: string;
  links: {
    team_type: "account_team_member" | "executive_team_member";
    account_team_member?: string;
    executive_team_member?: string;
    customer_team_member: string;
    relationship_percentage: number | null;
    is_priority: boolean;
  }[];
  transferDecisions?: {
    relationship_id: string;
    successor_user_id?: string | null;
    leave_dormant?: boolean;
  }[];
  handoffNote?: string;
}

/** FastAPI 422 detail items look like { type, loc, msg, input, ctx }. */
const extractApiDetailMessage = (detail: unknown, fallback: string): string => {
  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first && typeof first === "object") {
      const msg =
        (first as { msg?: unknown }).msg ??
        (first as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
  }

  if (detail && typeof detail === "object") {
    const msg =
      (detail as { msg?: unknown }).msg ??
      (detail as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }

  return fallback;
};

const parseApiErrorMessage = (error: any, fallback: string): string => {
  const raw = error?.message;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.message != null) {
        return extractApiDetailMessage(parsed.message, fallback);
      }
    } catch {
      return raw;
    }
  }

  const data = error?.response?.data;
  if (typeof data?.response?.message === "string" && data.response.message) {
    return data.response.message;
  }

  return extractApiDetailMessage(data?.message ?? data?.detail, fallback);
};

const parseBulkUploadMessage = (
  response: TeamBulkUploadApiResponse,
  fallback: string
): string => response?.response?.message || response?.message || fallback;

const isBulkUploadFailure = (response: TeamBulkUploadApiResponse): boolean => {
  const topStatus = String(response?.status || "").toLowerCase();
  if (topStatus === "failure" || topStatus === "unauthorized") return true;

  const nestedStatus = response?.response?.status;
  return nestedStatus != null && (nestedStatus < 200 || nestedStatus >= 300);
};

const normalizeBulkUploadResult = (
  response: TeamBulkUploadApiResponse,
  fallbackMessage: string
): TeamBulkUploadResult => ({
  content: response?.content ?? {},
  message: parseBulkUploadMessage(response, fallbackMessage),
});

export const fetchAccountDetails = createAsyncThunk(
  "accountDetails/fetch",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getCustomerById(accountId));
      return response as AccountDetails;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch account details";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAccountTeam = createAsyncThunk(
  "accountDetails/fetchAccountTeam",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getCustomerAccountTeam(accountId));
      const members = Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response)
          ? response
          : [];
      return members as AccountTeamMember[];
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch account team";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchCustomerTeam = createAsyncThunk(
  "accountDetails/fetchCustomerTeam",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getCustomerTeam(accountId));
      const members = Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response)
          ? response
          : [];
      return members as CustomerTeamMember[];
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch customer team";
      return rejectWithValue(errorMessage);
    }
  }
);

export const createAccountTeamMembersBulk = createAsyncThunk<
  TeamBulkUploadResult,
  CreateAccountTeamBulkPayload,
  { rejectValue: string }
>(
  "accountDetails/createAccountTeamMembersBulk",
  async ({ customerId, ...payload }, { rejectWithValue }) => {
    try {
      const response = (await post(
        API_URLS.createCustomerAccountTeamBulk(customerId),
        payload
      )) as TeamBulkUploadApiResponse;

      if (isBulkUploadFailure(response)) {
        return rejectWithValue(
          parseBulkUploadMessage(response, "Failed to add account team member")
        );
      }

      return normalizeBulkUploadResult(
        response,
        "Account team member request submitted"
      );
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to add account team member")
      );
    }
  }
);

export const createCustomerTeamMember = createAsyncThunk<
  { message: string },
  CreateCustomerTeamPayload,
  { rejectValue: string }
>(
  "accountDetails/createCustomerTeamMember",
  async ({ customerId, ...body }, { rejectWithValue }) => {
    try {
      await post(API_URLS.createCustomerTeamMember(customerId), body);
      return { message: "Customer team member added successfully" };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to add customer team member")
      );
    }
  }
);

export const fetchExecutiveTeamDetail = createAsyncThunk(
  "accountDetails/fetchExecutiveTeamDetail",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.getCustomerExtraSponsorsDetail(accountId)
      );
      return {
        sponsors: Array.isArray(response?.content?.sponsors)
          ? (response.content.sponsors as ExecutiveTeamMember[])
          : [],
        extraSponsors: Array.isArray(response?.content?.extra_sponsors)
          ? (response.content.extra_sponsors as ExecutiveTeamMember[])
          : [],
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to fetch executive team")
      );
    }
  }
);

export const createExtraSponsors = createAsyncThunk(
  "accountDetails/createExtraSponsors",
  async (
    { customerId, members }: CreateExtraSponsorsBulkPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.createCustomerExtraSponsors(customerId),
        {
          members,
        }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to add executive team members")
      );
    }
  }
);

export const deleteExtraSponsor = createAsyncThunk(
  "accountDetails/deleteExtraSponsor",
  async (
    { customerId, id }: { customerId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await del(
        API_URLS.deleteCustomerExtraSponsor(customerId, id)
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to delete executive team member")
      );
    }
  }
);

/**
 * Target state slice for organization users fetch.
 * - "executiveTeam": stores in executiveTeam.organizationUsers (for executive picker)
 * - "accountTeamCandidates": stores in accountTeamCandidates.items (for account team picker)
 */
export type OrganizationUsersTarget = "executiveTeam" | "accountTeamCandidates";

interface FetchOrganizationUsersParams {
  /** Optional comma-separated role names to filter by. Omit for all users. */
  roles?: string[];
  /** Which state slice to store results in. Defaults to "executiveTeam". */
  target?: OrganizationUsersTarget;
}

/**
 * Universal thunk to fetch organization users with optional role filtering.
 * Pass roles array to filter by specific role names, or omit for all users.
 * Use target to specify which state slice should be updated.
 */
export const fetchOrganizationUsers = createAsyncThunk(
  "accountDetails/fetchOrganizationUsers",
  async (
    params: FetchOrganizationUsersParams | undefined,
    { rejectWithValue }
  ) => {
    try {
      const { roles, target = "executiveTeam" } = params ?? {};
      const rolesParam = roles?.length ? roles.join(",") : undefined;
      const response = await get(
        API_URLS.getOrganizationUsers(rolesParam ? { roles: rolesParam } : {})
      );
      const users = Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response)
          ? response
          : [];
      return { users: users as OrganizationUser[], target };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to fetch organization users")
      );
    }
  }
);

export const fetchStakeholderRelationships = createAsyncThunk(
  "accountDetails/fetchStakeholderRelationships",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.getStakeholderRelationships(accountId)
      );
      const relationships = Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response)
          ? response
          : [];
      return relationships as StakeholderRelationship[];
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to fetch stakeholder relationships")
      );
    }
  }
);

export const saveStakeholderRelationships = createAsyncThunk(
  "accountDetails/saveStakeholderRelationships",
  async (
    {
      customerId,
      links,
      transferDecisions = [],
      handoffNote,
    }: SaveStakeholderLinksPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.getStakeholderRelationships(customerId),
        {
          links,
          transfer_decisions: transferDecisions,
          handoff_note: handoffNote,
        }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to save stakeholder relationships")
      );
    }
  }
);

export const deactivateTeamMember = createAsyncThunk(
  "accountDetails/deactivateTeamMember",
  async (
    {
      memberId,
      teamType,
    }: { memberId: string; teamType: "account" | "customer" },
    { rejectWithValue }
  ) => {
    try {
      const url =
        teamType === "account"
          ? API_URLS.deactivateAccountTeamMember(memberId)
          : API_URLS.deactivateCustomerTeamMember(memberId);
      const response = await put(url, {});
      return response;
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to deactivate team member")
      );
    }
  }
);

export const updateTeamMember = createAsyncThunk<
  TeamBulkUploadResult,
  {
    memberId: string;
    teamType: "account" | "customer";
    customerId?: string;
    payload: Record<string, any>;
  },
  { rejectValue: string }
>(
  "accountDetails/updateTeamMember",
  async ({ memberId, teamType, customerId, payload }, { rejectWithValue }) => {
    try {
      if (teamType === "account") {
        const response = (await post(
          API_URLS.updateAccountTeamMember(memberId),
          {
            ...payload,
            ...(customerId ? { customer_id: customerId } : {}),
          }
        )) as TeamBulkUploadApiResponse;

        if (isBulkUploadFailure(response)) {
          return rejectWithValue(
            parseBulkUploadMessage(
              response,
              "Failed to update account team member"
            )
          );
        }

        return normalizeBulkUploadResult(response, "Submitted for approval");
      }

      if (!customerId) {
        return rejectWithValue(
          "Account id is required to update customer team member"
        );
      }

      await put(
        API_URLS.updateCustomerTeamMemberById(customerId, memberId),
        payload
      );
      return {
        content: {},
        message: "Customer team member updated successfully",
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to update team member")
      );
    }
  }
);

const AccountDetailsSlice = createSlice({
  name: "accountDetails",
  initialState,
  reducers: {
    clearAccountDetailsState: state => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.accountTeam = initialAccountTeamState;
      state.customerTeam = initialCustomerTeamState;
      state.accountTeamCreate = {
        loading: false,
        error: null,
        success: false,
        message: null,
      };
      state.customerTeamCreate = {
        loading: false,
        error: null,
        success: false,
        message: null,
      };
      state.executiveTeam = {
        sponsors: [],
        extraSponsors: [],
        organizationUsers: [],
        loading: false,
        organizationUsersLoading: false,
        error: null,
        organizationUsersError: null,
      };
      state.executiveTeamCreate = {
        loading: false,
        error: null,
        success: false,
      };
      state.stakeholderRelationships = {
        items: [],
        loading: false,
        error: null,
        saving: false,
        saveError: null,
      };
      state.memberAction = {
        loading: false,
        error: null,
      };
    },
    clearAccountTeamCreateState: state => {
      state.accountTeamCreate = {
        loading: false,
        error: null,
        success: false,
        message: null,
      };
      state.customerTeamCreate = {
        loading: false,
        error: null,
        success: false,
        message: null,
      };
      state.executiveTeamCreate = {
        loading: false,
        error: null,
        success: false,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccountDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(fetchAccountDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAccountTeam.pending, state => {
        state.accountTeam.loading = true;
        state.accountTeam.error = null;
        state.accountTeam.members = [];
      })
      .addCase(fetchAccountTeam.fulfilled, (state, action) => {
        state.accountTeam.loading = false;
        state.accountTeam.error = null;
        state.accountTeam.members = action.payload;
      })
      .addCase(fetchAccountTeam.rejected, (state, action) => {
        state.accountTeam.loading = false;
        state.accountTeam.error = action.payload as string;
        state.accountTeam.members = [];
      })
      .addCase(fetchCustomerTeam.pending, state => {
        state.customerTeam.loading = true;
        state.customerTeam.error = null;
      })
      .addCase(fetchCustomerTeam.fulfilled, (state, action) => {
        state.customerTeam.loading = false;
        state.customerTeam.error = null;
        state.customerTeam.members = action.payload;
      })
      .addCase(fetchCustomerTeam.rejected, (state, action) => {
        state.customerTeam.loading = false;
        state.customerTeam.error = action.payload as string;
        state.customerTeam.members = [];
      })
      .addCase(createAccountTeamMembersBulk.pending, state => {
        state.accountTeamCreate.loading = true;
        state.accountTeamCreate.error = null;
        state.accountTeamCreate.success = false;
        state.accountTeamCreate.message = null;
      })
      .addCase(createAccountTeamMembersBulk.fulfilled, (state, action) => {
        state.accountTeamCreate.loading = false;
        state.accountTeamCreate.error = null;
        state.accountTeamCreate.success = true;
        state.accountTeamCreate.message = action.payload.message;
        toast.success(action.payload.message);
      })
      .addCase(createAccountTeamMembersBulk.rejected, (state, action) => {
        state.accountTeamCreate.loading = false;
        state.accountTeamCreate.error = action.payload as string;
        state.accountTeamCreate.success = false;
        state.accountTeamCreate.message = null;
        toast.error(
          (action.payload as string) || "Failed to add account team member"
        );
      })
      .addCase(createCustomerTeamMember.pending, state => {
        state.customerTeamCreate.loading = true;
        state.customerTeamCreate.error = null;
        state.customerTeamCreate.success = false;
        state.customerTeamCreate.message = null;
      })
      .addCase(createCustomerTeamMember.fulfilled, (state, action) => {
        state.customerTeamCreate.loading = false;
        state.customerTeamCreate.error = null;
        state.customerTeamCreate.success = true;
        state.customerTeamCreate.message = action.payload.message;
        toast.success(action.payload.message);
      })
      .addCase(createCustomerTeamMember.rejected, (state, action) => {
        state.customerTeamCreate.loading = false;
        state.customerTeamCreate.error = action.payload as string;
        state.customerTeamCreate.success = false;
        state.customerTeamCreate.message = null;
        toast.error(
          (action.payload as string) || "Failed to add customer team member"
        );
      })
      .addCase(fetchExecutiveTeamDetail.pending, state => {
        state.executiveTeam.loading = true;
        state.executiveTeam.error = null;
        state.executiveTeam.sponsors = [];
        state.executiveTeam.extraSponsors = [];
      })
      .addCase(fetchExecutiveTeamDetail.fulfilled, (state, action) => {
        state.executiveTeam.loading = false;
        state.executiveTeam.error = null;
        state.executiveTeam.sponsors = action.payload.sponsors;
        state.executiveTeam.extraSponsors = action.payload.extraSponsors;
      })
      .addCase(fetchExecutiveTeamDetail.rejected, (state, action) => {
        state.executiveTeam.loading = false;
        state.executiveTeam.error = action.payload as string;
        state.executiveTeam.sponsors = [];
        state.executiveTeam.extraSponsors = [];
      })
      .addCase(createExtraSponsors.pending, state => {
        state.executiveTeamCreate.loading = true;
        state.executiveTeamCreate.error = null;
        state.executiveTeamCreate.success = false;
      })
      .addCase(createExtraSponsors.fulfilled, state => {
        state.executiveTeamCreate.loading = false;
        state.executiveTeamCreate.error = null;
        state.executiveTeamCreate.success = true;
      })
      .addCase(createExtraSponsors.rejected, (state, action) => {
        state.executiveTeamCreate.loading = false;
        state.executiveTeamCreate.error = action.payload as string;
        state.executiveTeamCreate.success = false;
      })
      .addCase(fetchOrganizationUsers.pending, (state, action) => {
        const target = action.meta.arg?.target ?? "executiveTeam";
        if (target === "accountTeamCandidates") {
          state.accountTeamCandidates.loading = true;
          state.accountTeamCandidates.error = null;
        } else {
          state.executiveTeam.organizationUsersLoading = true;
          state.executiveTeam.organizationUsersError = null;
        }
      })
      .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
        const { users, target } = action.payload;
        if (target === "accountTeamCandidates") {
          state.accountTeamCandidates.loading = false;
          state.accountTeamCandidates.error = null;
          state.accountTeamCandidates.items = users;
        } else {
          state.executiveTeam.organizationUsersLoading = false;
          state.executiveTeam.organizationUsersError = null;
          state.executiveTeam.organizationUsers = users;
        }
      })
      .addCase(fetchOrganizationUsers.rejected, (state, action) => {
        const target = action.meta.arg?.target ?? "executiveTeam";
        if (target === "accountTeamCandidates") {
          state.accountTeamCandidates.loading = false;
          state.accountTeamCandidates.error = action.payload as string;
          state.accountTeamCandidates.items = [];
        } else {
          state.executiveTeam.organizationUsersLoading = false;
          state.executiveTeam.organizationUsersError = action.payload as string;
          state.executiveTeam.organizationUsers = [];
        }
      })
      .addCase(fetchStakeholderRelationships.pending, state => {
        state.stakeholderRelationships.loading = true;
        state.stakeholderRelationships.error = null;
      })
      .addCase(fetchStakeholderRelationships.fulfilled, (state, action) => {
        state.stakeholderRelationships.loading = false;
        state.stakeholderRelationships.error = null;
        state.stakeholderRelationships.items = action.payload;
      })
      .addCase(fetchStakeholderRelationships.rejected, (state, action) => {
        state.stakeholderRelationships.loading = false;
        state.stakeholderRelationships.error = action.payload as string;
        state.stakeholderRelationships.items = [];
      })
      .addCase(saveStakeholderRelationships.pending, state => {
        state.stakeholderRelationships.saving = true;
        state.stakeholderRelationships.saveError = null;
      })
      .addCase(saveStakeholderRelationships.fulfilled, state => {
        state.stakeholderRelationships.saving = false;
        state.stakeholderRelationships.saveError = null;
      })
      .addCase(saveStakeholderRelationships.rejected, (state, action) => {
        state.stakeholderRelationships.saving = false;
        state.stakeholderRelationships.saveError = action.payload as string;
      })
      .addCase(deleteExtraSponsor.pending, state => {
        state.memberAction.loading = true;
        state.memberAction.error = null;
      })
      .addCase(deleteExtraSponsor.fulfilled, state => {
        state.memberAction.loading = false;
        state.memberAction.error = null;
      })
      .addCase(deleteExtraSponsor.rejected, (state, action) => {
        state.memberAction.loading = false;
        state.memberAction.error = action.payload as string;
      })
      .addCase(deactivateTeamMember.pending, state => {
        state.memberAction.loading = true;
        state.memberAction.error = null;
      })
      .addCase(deactivateTeamMember.fulfilled, state => {
        state.memberAction.loading = false;
        state.memberAction.error = null;
      })
      .addCase(deactivateTeamMember.rejected, (state, action) => {
        state.memberAction.loading = false;
        state.memberAction.error = action.payload as string;
      })
      .addCase(updateTeamMember.pending, state => {
        state.memberAction.loading = true;
        state.memberAction.error = null;
      })
      .addCase(updateTeamMember.fulfilled, (state, action) => {
        state.memberAction.loading = false;
        state.memberAction.error = null;
        toast.success(action.payload.message);
      })
      .addCase(updateTeamMember.rejected, (state, action) => {
        state.memberAction.loading = false;
        state.memberAction.error = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to update team member"
        );
      });
  },
});

export const { clearAccountDetailsState, clearAccountTeamCreateState } =
  AccountDetailsSlice.actions;
export default AccountDetailsSlice.reducer;
